import { getLocalPath } from '../../spa/utility/config/configuration'
import { showAlertBanner } from '../AlertBanner/index.js'
import { STATUS_ERROR } from '../AlertBanner/Constants.js'
import {
  FUND_DETAILS_ERROR_MESSAGE_TEXT,
  ERROR_MESSAGE_TITLE,
} from '../../spa/components/investmentProducts/helpers/ErrorMessages.js'
const apiBasePath = `${getLocalPath()}/halapi/documents`

export const fetchData = async (url) => {
  try {
    const response = await fetch(url)
    if (!response.ok) {
      console.log(JSON.stringify(response.status, url))
      throw FUND_DETAILS_ERROR_MESSAGE_TEXT
    }
    return await response.json()
  } catch (err) {
    showAlertBanner({
      status: STATUS_ERROR,
      title: ERROR_MESSAGE_TITLE,
      text: FUND_DETAILS_ERROR_MESSAGE_TEXT,
    })
  }
}

let productTooltipData = {}
export const fetchTooltipData = async () => {
  if (Object.keys(productTooltipData).length) {
    return productTooltipData
  }
  productTooltipData.firstCall = true

  const tooltips = {
    'cti-common.product-tooltip.etf-tooltip': 'etfdata',
    'cti-common.product-tooltip.variable-portfolio-tooltip': 'variableData',
    'cti-common.product-tooltip.mutual-fund-tooltip': 'mutualFundData',
    'cti-common.product-tooltip.model-portfolio-tooltip': 'modelPortfoliosData',
  }
  const url = `${apiBasePath}?_scope=/content/documents/administration/cti-common/product-tooltip`
  const res = await fetchData(url)
  productTooltipData = (res?._embedded?.documents || []).reduce(
    (p, n) => ({ ...p, [tooltips[n?.basename]]: n?.bundles?.default }),
    {}
  )
}

export const tooltipCMSMapping = {
  // ETF's
  'Market Price': {
    heading: 'market-price-heading',
    content: 'market-price-content',
  },
  'Median Bid-Ask Spread': {
    heading: 'median-bid-ask-spread-heading',
    content: 'median-bid-ask-spread-content',
  },
  'Tracking Basket': {
    heading: 'tracking-basket-heading',
    content: 'tracking-basket-content',
  },
  'Tracking Basket Weight Overlap': {
    heading: 'tracking-basket-weight-overlap-heading',
    content: 'tracking-basket-weight-overlap-content',
  },
  'Holdings Quantity': {
    heading: 'top-holdings-quantity-heading',
    content: 'top-holdings-quantity-content',
  },
  // Model Portfolio
  'Average Underlying Expenses': {
    heading: 'average-underlying-expenses-heading',
    content: 'average-underlying-expenses-content',
  },
  'Trailing 12-Month Yield': {
    heading: 'trailing-12-month-yield-heading',
    content: 'trailing-12-month-yield-content',
  },
  // Variable Portfolio
  'Net Expense Ratio': {
    heading: 'net-expense-ratio-heading',
    content: 'net-expense-ratio-content',
  },
  'Gross Expense Ratio': {
    heading: 'gross-expense-ratio-heading',
    content: 'gross-expense-ratio-content',
  },
  // Mutual Fund's
  'Active Share': {
    heading: 'active-share-heading',
    content: 'active-share-content',
  },
  'Alpha 3-year': {
    heading: 'alpha-heading',
    content: 'alpha-content',
  },
  'Annual distribution rate at NAV': {
    heading: 'annual-distribution-rate-heading',
    content: 'annual-distribution-rate-content',
  },
  'Average Effective Duration': {
    heading: 'average-effective-duration-heading',
    content: 'average-effective-duration-content',
  },
  'Beta vs. benchmark index (3-yr returns)': {
    heading: 'beta-heading',
    content: 'beta-content',
  },
  'P/B (wtd avg.)': {
    heading: 'price-book-ratio-heading',
    content: 'price-book-ratio-content',
  },
  'P/E (wtd avg., trailing 12 mos)': {
    heading: 'price-earning-ratio-heading',
    content: 'price-earning-ratio-content',
  },
  'R-squared 3-year': {
    heading: 'r-squared-heading',
    content: 'r-squared-content',
  },
  '7-day current': {
    heading: '7-day-current-yield-heading',
    content: '7-day-current-yield-content',
  },
  '7-day unsubsidized': {
    heading: '7-day-unsubsidized-heading',
    content: '7-day-unsubsidized-content',
  },
  '7-day effective': {
    heading: '7-day-effective-yield-heading',
    content: '7-day-effective-yield-content',
  },
  '30-day SEC yield': {
    heading: '30-day-sec-yield-heading',
    content: '30-day-sec-yield-content',
  },
  'Unsubsidized 30-day SEC yield': {
    heading: 'unsubsidized-30-day-sec-yield-heading',
    content: 'unsubsidized-30-day-sec-yield-content',
  },
  'Sharpe Ratio 3-year': {
    heading: 'sharpe-ratio-heading',
    content: 'sharpe-ratio-content',
  },
  'Standard Deviation 3-year': {
    heading: 'standard-deviation-heading',
    content: 'standard-deviation-content',
  },
  'Turnover rate (fiscal year-end %)': {
    heading: 'turnover-rate-heading',
    content: 'turnover-rate-content',
  },
  'Average Coupon (%)': {
    heading: 'average-coupon-heading',
    content: 'average-coupon-content',
  },
  'Yield to Worst': {
    heading: 'yield-to-worst-heading',
    content: 'yield-to-worst-content',
  },
}

export function getTooltipContentByKey(data, key) {
  const heading = data[tooltipCMSMapping[key]?.heading] || ''
  const content = data[tooltipCMSMapping[key]?.content] || ''

  return {
    heading,
    content,
  }
}

export function getETFTooltipContent() {
  return productTooltipData?.etfdata || {}
}

export function getVariablePortfolioTooltipContent() {
  return productTooltipData?.variableData || {}
}

export function getModelPortfolioTooltipContent() {
  return productTooltipData?.modelPortfoliosData || {}
}

export function getMutualFundTooltipContent() {
  return productTooltipData?.mutualFundData || {}
}

export async function getEtfHoldingsDisclousreData() {
  const url = `${apiBasePath}?_scope=/content/documents/cti-advisor/common/holdings-disclosure/etf-holdings-disclosure`
  const data = await fetchData(url)
  return data?._embedded?.documents[0]?.content?.content ?? ''
}

export async function getIntervalFundHoldingsDisclousreData() {
  const url = `${apiBasePath}?_scope=/content/documents/cti-advisor/common/holdings-disclosure/interval-fund-holdings-disclosure`
  const data = await fetchData(url)
  return data?._embedded?.documents[0]?.content?.content ?? ''
}
